/**
 * The i18n instance, given its catalogue (04-architecture § 10).
 *
 * Separate from `index.ts` on purpose: that module imports the whole catalogue
 * for the apps, and the site's pages must not download the backoffice's and
 * the app's copy to show one screen (08 § 12). `@cowoo/shared/i18n-site` hands
 * this function only the namespaces a web page can reach.
 */

import i18next, { type i18n as I18n } from 'i18next';
import { initReactI18next } from 'react-i18next';
import { formatDayShort, formatDayLong, formatMoney, formatMoneyShort, formatTime } from '../format/index.js';

export type Platform = 'web' | 'native';

export function initI18nWith(
  platform: Platform,
  catalogue: Record<string, unknown>,
  instance: I18n = i18next,
): I18n {
  if (instance.isInitialized) {
    // A module re-evaluated by a dev server (new copy in `de.json`) meets the
    // instance it initialised before: hand it the fresh catalogue instead of
    // keeping the old one until a restart.
    instance.addResourceBundle('de', 'translation', catalogue, true, true);
    return instance;
  }

  void instance.use(initReactI18next).init({
    lng: 'de',
    fallbackLng: 'de',
    returnNull: false,
    resources: { de: { translation: catalogue } },
    interpolation: {
      // React escapes for us on both platforms.
      escapeValue: false,
    },
    // Missing keys are a bug: fail loudly while developing, degrade in production.
    saveMissing: false,
    parseMissingKeyHandler: (key) => {
      if (process.env.NODE_ENV !== 'production') throw new Error(`missing i18n key: ${key}`);
      return key;
    },
    react: { useSuspense: platform === 'web' },
  });

  instance.services.formatter?.add('money', (value) => formatMoney(Number(value)));
  instance.services.formatter?.add('moneyShort', (value) => formatMoneyShort(Number(value)));
  instance.services.formatter?.add('dayShort', (value) => formatDayShort(String(value)));
  instance.services.formatter?.add('dayLong', (value) => formatDayLong(String(value)));
  instance.services.formatter?.add('time', (value) => formatTime(String(value)));

  return instance;
}
